export const MEDIA_LIMITS = {
  video: 500 * 1024 * 1024,
  poster: 10 * 1024 * 1024,
};

export const MEDIA_TYPES = {
  video: ["video/mp4", "video/quicktime", "video/x-quicktime"],
  poster: ["image/jpeg", "image/png"],
};

export const TRANSCRIPT_MAX_LENGTH = 100000;

export const EDITORIAL_SECTIONS = [
  { value: "practice", label: "Practice" },
  { value: "method", label: "Method" },
  { value: "foundations", label: "Foundations" },
];

const blockedRichTextTags = "script,style,iframe,object,embed,svg,math,template,noscript";
const allowedRichTextTags = new Set([
  "A", "B", "BLOCKQUOTE", "BR", "DIV", "EM", "H1", "H2", "H3", "H4", "H5", "H6",
  "I", "LI", "OL", "P", "STRONG", "U", "UL",
]);

const decodeFallbackEntities = (value) =>
  String(value || "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'");

export const richTextToPlainText = (value) => {
  const html = String(value || "");
  if (typeof DOMParser === "undefined") {
    return decodeFallbackEntities(
      html
        .replace(new RegExp(`<(?:${blockedRichTextTags.replace(/,/g, "|")})\\b[^>]*>[\\s\\S]*?<\\/(?:${blockedRichTextTags.replace(/,/g, "|")})>`, "gi"), "")
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/<[^>]*>/g, " ")
    ).replace(/\s+/g, " ").trim();
  }
  const parsed = new DOMParser().parseFromString(html, "text/html");
  parsed.querySelectorAll(blockedRichTextTags).forEach((node) => node.remove());
  return (parsed.body.textContent || "").replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
};

const isSafeRichTextHref = (href) => {
  const value = String(href || "").trim();
  if (/^(?:#|\/|\.\/|\.\.\/)/.test(value)) return true;
  try {
    return ["http:", "https:", "mailto:", "tel:"].includes(new URL(value).protocol);
  } catch {
    return false;
  }
};

export const sanitizeRichTextForPreview = (value) => {
  if (typeof DOMParser === "undefined") return richTextToPlainText(value);
  const parsed = new DOMParser().parseFromString(String(value || ""), "text/html");
  parsed.querySelectorAll(blockedRichTextTags).forEach((node) => node.remove());
  Array.from(parsed.body.querySelectorAll("*")).forEach((element) => {
    if (!allowedRichTextTags.has(element.tagName)) {
      const parent = element.parentNode;
      if (parent) {
        while (element.firstChild) parent.insertBefore(element.firstChild, element);
        parent.removeChild(element);
      }
      return;
    }
    const href = element.tagName === "A" ? element.getAttribute("href") || "" : "";
    Array.from(element.attributes).forEach((attribute) => element.removeAttribute(attribute.name));
    if (element.tagName === "A") {
      if (isSafeRichTextHref(href)) {
        element.setAttribute("href", href);
        if (!href.startsWith("/") && !href.startsWith("#")) {
          element.setAttribute("target", "_blank");
          element.setAttribute("rel", "noopener noreferrer");
        }
      }
    }
  });
  return parsed.body.innerHTML.trim();
};

export const emptyMediaForm = (kind) => ({
  title: "",
  summary: "",
  description: "",
  transcript: "",
  topic: "",
  section: "",
  categories: [],
  tags: [],
  channels: ["leaderbridge"],
  featured: false,
  featuredOrder: "",
  posterAlt: "",
  seoTitle: "",
  seoDescription: "",
  kind,
});

export const mediaToForm = (media, kind) => ({
  ...emptyMediaForm(kind),
  title: media?.title || "",
  summary: media?.summary || "",
  description: media?.description || "",
  transcript: media?.transcript || "",
  topic: media?.topic || "",
  section: media?.section || "",
  categories: Array.isArray(media?.categories) ? media.categories : [],
  tags: Array.isArray(media?.tags) ? media.tags : [],
  channels: Array.isArray(media?.channels) && media.channels.length
    ? media.channels
    : ["leaderbridge"],
  featured: Boolean(media?.featured),
  featuredOrder: media?.featuredOrder ?? "",
  posterAlt: media?.poster?.alt || "",
  seoTitle: media?.seo?.title || "",
  seoDescription: media?.seo?.description || "",
  kind: media?.kind || kind,
});

export const buildMediaPayload = (form, kind) => ({
  title: form.title.trim(),
  kind,
  summary: form.summary.trim(),
  description: form.description.trim(),
  transcript: String(form.transcript || "").replace(/\r\n?/g, "\n").trim(),
  topic: form.topic.trim(),
  section: form.section || null,
  categories: form.categories.map((value) => value.trim()).filter(Boolean),
  tags: form.tags.map((value) => value.trim()).filter(Boolean),
  channels: form.channels,
  featured: Boolean(form.featured),
  featuredOrder:
    form.featured && form.featuredOrder !== ""
      ? Number(form.featuredOrder)
      : null,
  posterAlt: form.posterAlt.trim(),
  seoTitle: form.seoTitle.trim(),
  seoDescription: form.seoDescription.trim(),
});

export const validateMediaForm = (form, { mode = "continue" } = {}) => {
  const errors = {};
  if (!form.title.trim()) errors.title = "Enter a title to continue.";
  if (mode !== "draft") {
    if (!richTextToPlainText(form.summary)) {
      errors.summary = "Add a short summary so readers know what this post is about.";
    }
    if (!form.topic.trim()) errors.topic = "Choose a topic before publishing.";
    if (!form.section) errors.section = "Choose one publishing section before publishing.";
  }
  if (!form.channels.length) errors.channels = "Choose where this post should appear.";
  if (String(form.transcript || "").length > TRANSCRIPT_MAX_LENGTH) {
    errors.transcript = `Keep the transcript under ${TRANSCRIPT_MAX_LENGTH.toLocaleString()} characters.`;
  }
  if (
    form.featuredOrder !== "" &&
    (!Number.isInteger(Number(form.featuredOrder)) || Number(form.featuredOrder) < 0)
  ) {
    errors.featuredOrder = "Featured order must be a whole number of 0 or greater.";
  }
  return errors;
};

export const validateMediaField = (form, field, options) =>
  validateMediaForm(form, options)[field];

export const validateUploadFile = (file, assetType) => {
  if (!file) return "Choose a file to upload.";
  if (!normalizeUploadContentType(file, assetType)) {
    return assetType === "video"
      ? "Choose an MP4 or MOV video. The backend securely inspects and converts supported device video."
      : "Choose a JPEG or PNG poster.";
  }
  if (file.size <= 0) return "The selected file is empty.";
  if (file.size > MEDIA_LIMITS[assetType]) {
    return `${assetType === "video" ? "Video" : "Poster"} exceeds the default ${
      MEDIA_LIMITS[assetType] / 1024 / 1024
    } MB limit. The backend may enforce a stricter configured limit.`;
  }
  return "";
};

const fileExtension = (file) => {
  const match = String(file?.name || "").toLowerCase().match(/\.([a-z0-9]+)$/);
  return match ? match[1] : "";
};

export const normalizeUploadContentType = (file, assetType) => {
  const declared = String(file?.type || "").trim().toLowerCase();
  if (assetType === "poster") {
    return MEDIA_TYPES.poster.includes(declared) ? declared : "";
  }

  const extension = fileExtension(file);
  if (extension === "mov") return "video/quicktime";
  if (extension === "mp4") return "video/mp4";
  if (declared === "video/x-quicktime") return "video/quicktime";
  return ["video/mp4", "video/quicktime"].includes(declared) ? declared : "";
};

const bufferToHex = (buffer) =>
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");

const readFile = (file) => {
  if (typeof file.arrayBuffer === "function") return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("The file could not be read."));
    reader.readAsArrayBuffer(file);
  });
};

const SHA256_INITIAL = [
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
  0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
];

const SHA256_CONSTANTS = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

const rotateRight = (value, count) => (value >>> count) | (value << (32 - count));

class IncrementalSha256 {
  constructor() {
    this.state = new Uint32Array(SHA256_INITIAL);
    this.block = new Uint8Array(64);
    this.blockLength = 0;
    this.bytesHashed = 0;
    this.words = new Uint32Array(64);
  }

  compress(block) {
    const words = this.words;
    for (let index = 0; index < 16; index += 1) {
      const offset = index * 4;
      words[index] = ((block[offset] << 24) | (block[offset + 1] << 16) |
        (block[offset + 2] << 8) | block[offset + 3]) >>> 0;
    }
    for (let index = 16; index < 64; index += 1) {
      const previous15 = words[index - 15];
      const previous2 = words[index - 2];
      const sigma0 = rotateRight(previous15, 7) ^ rotateRight(previous15, 18) ^ (previous15 >>> 3);
      const sigma1 = rotateRight(previous2, 17) ^ rotateRight(previous2, 19) ^ (previous2 >>> 10);
      words[index] = (words[index - 16] + sigma0 + words[index - 7] + sigma1) >>> 0;
    }

    let [a, b, c, d, e, f, g, h] = this.state;
    for (let index = 0; index < 64; index += 1) {
      const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choice = (e & f) ^ (~e & g);
      const first = (h + sum1 + choice + SHA256_CONSTANTS[index] + words[index]) >>> 0;
      const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const second = (sum0 + majority) >>> 0;
      h = g; g = f; f = e; e = (d + first) >>> 0;
      d = c; c = b; b = a; a = (first + second) >>> 0;
    }
    [a, b, c, d, e, f, g, h].forEach((value, index) => {
      this.state[index] = (this.state[index] + value) >>> 0;
    });
  }

  update(input) {
    const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
    this.bytesHashed += bytes.length;
    let offset = 0;
    while (offset < bytes.length) {
      const length = Math.min(64 - this.blockLength, bytes.length - offset);
      this.block.set(bytes.subarray(offset, offset + length), this.blockLength);
      this.blockLength += length;
      offset += length;
      if (this.blockLength === 64) {
        this.compress(this.block);
        this.blockLength = 0;
      }
    }
  }

  digestHex() {
    const bitLength = this.bytesHashed * 8;
    this.block[this.blockLength] = 0x80;
    this.block.fill(0, this.blockLength + 1);
    if (this.blockLength >= 56) {
      this.compress(this.block);
      this.block.fill(0);
    }
    const high = Math.floor(bitLength / 0x100000000);
    const low = bitLength >>> 0;
    for (let index = 0; index < 4; index += 1) {
      this.block[56 + index] = (high >>> (24 - index * 8)) & 0xff;
      this.block[60 + index] = (low >>> (24 - index * 8)) & 0xff;
    }
    this.compress(this.block);
    return Array.from(this.state)
      .map((word) => word.toString(16).padStart(8, "0"))
      .join("");
  }
}

const sha256FileInChunks = async (file, chunkSize) => {
  const hash = new IncrementalSha256();
  for (let offset = 0; offset < file.size; offset += chunkSize) {
    const chunk = file.slice(offset, Math.min(file.size, offset + chunkSize));
    hash.update(await readFile(chunk));
  }
  return hash.digestHex();
};

export const sha256File = async (
  file,
  cryptoApi = window.crypto,
  { chunkSize = 4 * 1024 * 1024, streamingThresholdBytes = 16 * 1024 * 1024 } = {}
) => {
  if (!cryptoApi?.subtle) {
    throw new Error("Secure SHA-256 hashing is unavailable. Use a modern browser over HTTPS.");
  }
  if (Number(file?.size) > streamingThresholdBytes && typeof file?.slice === "function") {
    return sha256FileInChunks(file, chunkSize);
  }
  const digest = await cryptoApi.subtle.digest("SHA-256", await readFile(file));
  return bufferToHex(digest);
};

export const uploadToSignedUrl = ({ upload, file, onProgress, xhrFactory, signal }) =>
  new Promise((resolve, reject) => {
    const xhr = xhrFactory ? xhrFactory() : new XMLHttpRequest();
    let settled = false;
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      if (signal) signal.removeEventListener("abort", abortUpload);
      callback(value);
    };
    const cancelledError = () => {
      const error = new Error("The upload was cancelled.");
      error.code = "UPLOAD_CANCELLED";
      return error;
    };
    const abortUpload = () => xhr.abort();
    if (signal?.aborted) {
      finish(reject, cancelledError());
      return;
    }
    if (signal) signal.addEventListener("abort", abortUpload, { once: true });
    xhr.open(upload.method || "PUT", upload.uploadUrl, true);
    Object.entries(upload.requiredHeaders || {}).forEach(([name, value]) => {
      xhr.setRequestHeader(name, value);
    });
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onerror = () => finish(reject, new Error("The direct storage upload failed. Check the network and retry."));
    xhr.onabort = () => finish(reject, cancelledError());
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) finish(resolve);
      else if (xhr.status === 403) {
        finish(reject, new Error("The upload authorization was rejected or expired. Request a new upload and retry."));
      } else {
        finish(reject, new Error(`The storage upload failed with status ${xhr.status || "unknown"}.`));
      }
    };
    xhr.send(file);
  });

export const canDeleteMedia = (media) =>
  media?.status === "draft" &&
  ["pending", "failed"].includes(media?.assetStatus) &&
  !media?.video?.hasOriginalAsset &&
  !media?.video?.hasPlaybackAsset &&
  !media?.poster?.hasAsset &&
  !media?.video?.objectKey &&
  !media?.video?.playbackKey &&
  !media?.poster?.objectKey;

export const canPublishMedia = (media) =>
  media?.status === "draft" &&
  media?.assetStatus === "ready" &&
  media?.publishReadiness?.ready !== false;

export const getPublishDisabledReason = (media) => {
  if (canPublishMedia(media)) return "Publish";
  if (!media || media.status !== "draft") {
    return media?.status === "published"
      ? "This media is already published"
      : "Only draft media can be published";
  }
  if (
    media.publishReadiness?.errors?.some((error) =>
      String(error).includes("public playback resource")
    )
  ) {
    return "Public media delivery is not configured on the server";
  }
  if (media.assetStatus === "failed") {
    return media.assetError?.message || "The media upload needs attention";
  }
  if (media.kind === "short" && media.assetStatus === "processing") {
    return media.video?.processingProgress?.message || "The Short is still being prepared";
  }
  if (!media.video?.verifiedAt) return "Upload and verify the video file";
  if (!media.poster?.verifiedAt) return "Upload and verify the poster image";
  return "The backend is finalizing publication readiness";
};

const hasVerifiedAsset = (media, assetType) =>
  Boolean(assetType === "poster" ? media?.poster?.verifiedAt : media?.video?.verifiedAt);

export const isAssetUploadActive = (media, assetType) =>
  ["created", "verifying", "processing"].includes(
    media?.assetUploads?.[assetType]?.status
  );

export const getPublicationReadiness = (media, form, hasUnsavedChanges = false) => {
  const isShort = (media?.kind || form?.kind) === "short";
  const videoVerified = hasVerifiedAsset(media, "video");
  const posterVerified = hasVerifiedAsset(media, "poster");
  const videoUploadActive = isAssetUploadActive(media, "video");
  const posterUploadActive = isAssetUploadActive(media, "poster");
  const contentComplete = Boolean(
    form?.title?.trim() &&
      richTextToPlainText(form?.summary) &&
      form?.topic?.trim() &&
      form?.section
  );
  const distributionComplete = Boolean(form?.channels?.length);
  const detailsComplete = contentComplete && distributionComplete;
  const processing = videoUploadActive || (media?.assetStatus === "processing" && !videoVerified);
  const failed = media?.assetStatus === "failed" && !videoVerified;
  const deliveryNotConfigured = media?.publishReadiness?.errors?.some((error) =>
    String(error).includes("public playback resource")
  );
  const blockers = [];

  if (!media?._id) blockers.push("Save the draft to start uploading.");
  if (!detailsComplete) {
    blockers.push("Add a title, summary, topic, publishing section, and at least one destination.");
  }
  if (hasUnsavedChanges && media?._id) blockers.push("Save your latest detail changes.");
  if (videoUploadActive) blockers.push("Your video is still being prepared.");
  if (posterUploadActive) blockers.push("Your thumbnail is still being verified.");
  if (failed) {
    blockers.push(media?.assetError?.message || "Resolve the failed media upload.");
  } else if (processing) {
    blockers.push("Wait for video preparation and verification to finish.");
  } else if (!videoVerified) {
    blockers.push("Upload and verify the video file.");
  }
  if (!posterVerified) blockers.push("Upload and verify the poster image.");
  if (deliveryNotConfigured) {
    blockers.push(
      "Public media delivery is not configured on the server (MEDIA_PUBLIC_BASE_URL)."
    );
  }
  if (
    videoVerified &&
    posterVerified &&
    media?.status === "draft" &&
    media?.assetStatus !== "ready"
  ) {
    blockers.push("The backend is finalizing publication readiness. Refresh the status.");
  }
  if (media?.status && media.status !== "draft") {
    blockers.push(
      media.status === "published"
        ? "This media is already published."
        : "Archived media cannot be published."
    );
  }

  return {
    blockers: [...new Set(blockers)],
    canPublish:
      canPublishMedia(media) &&
      detailsComplete &&
      !hasUnsavedChanges &&
      !failed &&
      !videoUploadActive &&
      !posterUploadActive,
    steps: [
      {
        key: "details",
        label: "Content details",
        complete: Boolean(media?._id && contentComplete && !hasUnsavedChanges),
        state: hasUnsavedChanges ? "action" : contentComplete && media?._id ? "complete" : "required",
        detail: !media?._id
          ? "Save the draft first"
          : hasUnsavedChanges
          ? "Save your latest changes"
          : contentComplete
          ? "Saved and complete"
          : "Title, summary, topic, and publishing section are required",
      },
      {
        key: "video",
        label: isShort ? "Short video" : "Video file",
        complete: videoVerified && !videoUploadActive,
        state: failed ? "error" : processing ? "processing" : videoVerified ? "complete" : "required",
        detail: failed
          ? media?.assetError?.message || "Upload failed"
          : processing
          ? media?.video?.processingProgress?.message || "Backend video preparation in progress"
          : videoVerified
          ? "Backend verified"
          : "Upload required",
      },
      {
        key: "poster",
        label: "Poster image",
        complete: posterVerified && !posterUploadActive,
        state: posterUploadActive ? "processing" : posterVerified ? "complete" : "required",
        detail: posterUploadActive ? "Thumbnail verification in progress" : posterVerified ? "Backend verified" : "JPEG or PNG required",
      },
      {
        key: "distribution",
        label: "Distribution",
        complete: distributionComplete,
        state: distributionComplete ? "complete" : "required",
        detail: distributionComplete
          ? `${form.channels.length} destination${form.channels.length === 1 ? "" : "s"} selected`
          : "Choose at least one destination",
      },
      {
        key: "publish",
        label: "Publish",
        complete: media?.status === "published",
        state:
          media?.status === "published"
            ? "complete"
            : canPublishMedia(media) && detailsComplete && !hasUnsavedChanges && !videoUploadActive && !posterUploadActive
            ? "ready"
            : "locked",
        detail:
          media?.status === "published"
            ? "Live on selected destinations"
            : canPublishMedia(media) && detailsComplete && !hasUnsavedChanges && !videoUploadActive && !posterUploadActive
            ? "Ready to publish"
            : "Complete the required steps above",
      },
    ],
  };
};

export const canUploadMediaAsset = (media, assetType) => {
  if (!media?._id || media.status === "archived") return false;
  return ["draft", "published"].includes(media.status);
};

export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined || seconds === "" || !(Number(seconds) >= 0)) {
    return "—";
  }
  const total = Math.round(Number(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const remainder = total % 60;
  return hours
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`
    : `${minutes}:${String(remainder).padStart(2, "0")}`;
};

export const formatFileSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size < 0) return "—";
  if (size < 1024) return `${size} B`;
  const units = ["KB", "MB", "GB"];
  let value = size / 1024;
  let unit = units[0];
  for (let index = 1; index < units.length && value >= 1024; index += 1) {
    value /= 1024;
    unit = units[index];
  }
  return `${value >= 10 ? value.toFixed(1) : value.toFixed(2)} ${unit}`;
};

export const getInitialMediaWorkflowStep = (media) => {
  if (!media?._id) return 0;
  if (["published", "archived"].includes(media.status)) return 2;
  if (media.assetStatus === "ready") return 2;
  return 1;
};

export const formatMediaDate = (value) =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(new Date(value))
    : "—";

export const assetStatusMessage = (status) => ({
  pending: "Waiting for verified uploads",
  uploaded: "Uploaded; verification is starting",
  processing: "Backend verification is in progress",
  ready: "Video and poster are verified",
  failed: "Verification failed; create a new upload intent to retry",
}[status] || "Asset status is unavailable");
